/**
 * Admin API client.
 * - Stores the JWT in localStorage (key: o2admin_token).
 * - Adds Authorization: Bearer <token> header to every request.
 * - On 401, clears the token so the AuthContext can redirect to /o2-control-panel.
 */
import axios from 'axios';

const TOKEN_KEY = 'o2admin_token';
const USER_KEY = 'o2admin_user';

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const getStoredUser = () => {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};
export const setSession = (user, token) => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
};
export const clearSession = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

const adminApi = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api/admin`,
});

adminApi.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

adminApi.interceptors.response.use(
  (resp) => resp,
  (error) => {
    // Only force the user back to login if the 401 is on a PROTECTED request.
    // A 401 on the /login endpoint itself is just "wrong credentials" — let it
    // bubble up so the Login component can show the inline error.
    const reqUrl = error.config?.url || '';
    const isAuthBoundary = reqUrl.endsWith('/login');
    if (error.response?.status === 401 && !isAuthBoundary) {
      clearSession();
      if (window.location.pathname.startsWith('/o2-control-panel')) {
        window.location.href = '/o2-control-panel';
      }
    }
    return Promise.reject(error);
  },
);

export default adminApi;

/**
 * Public-facing tracking client (no auth) for phone/whatsapp button clicks
 * + lead-form persistence. Used by the marketing site.
 */
export const trackEvent = (type, location) => {
  try {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type, location }),
      // keepalive ensures the request still fires on navigation
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* never break UX for analytics */
  }
};
